<template>
    <b-row>
        <b-col md="12">
            <div>
                <b-alert
                    v-height-fade
                    show
                    dismissible
                    fade
                    variant="warning"
                >
                    <div class="alert-body">
                        <span>
                            {{ $t('org.message["The widgets provided in the Summary menu will now be available in the newly added Dashboard menu, so the Summary menu will be discontinued."]') }}
                        </span>
                    </div>
                </b-alert>
            </div>
        </b-col>
        <b-col :xl="3" :lg="4" :md="5">
            <b-overlay
                :show="status === 'loading'"
            >
                <SideBar
                    :month="month"
                    :months="months"
                    :allMonths="allMonths"
                    :current="currentSummary"
                    :estimated="estimatedSummary"
                    :status="status.toString()"
                    :theme="theme"
                    @change-month="changeMonth"
                >
                    <template  v-if="status === 200 || status === 206" slot="download">
                        <b-row class="mt-3">
                            <b-col :cols="4" class="text-center">
                                <b-button
                                    class="btn-circle"
                                    :style="disabledStyle"
                                    variant="info"
                                    @click="downloadSimpleExcel"
                                    :disabled="disabledButton"
                                >
                                    <i v-if="downloadLoadingSimple" class="fas fa-spinner fa-spin fa-fw fa-lg"></i>
                                    <i v-else class="fas fa-file-excel fa-fw fa-lg"></i>
                                </b-button>
                                <p>
                                    <small>
                                        {{ $t('org.billing.XLSX of Usage Summary') }}
                                    </small>
                                </p>
                            </b-col>
                            <b-col :cols="4" class="text-center">
                                <b-button
                                    class="btn-circle"
                                    :style="disabledStyle"
                                    variant="success"
                                    @click="showDownloadDetail"
                                    :disabled="disabledButton"
                                >
                                    <i v-if="downloadLoadingDetail" class="fas fa-spinner fa-spin fa-fw fa-lg"></i>
                                    <i v-else class="fas fa-file-csv fa-fw fa-lg"></i>
                                </b-button>
                                <p>
                                    <small>
                                        {{ $t('org.billing.CSV of Usage Details') }}
                                    </small>
                                </p>
                            </b-col>
                            <b-col :cols="4" class="text-center">
                                <b-button
                                    v-if="hasInvoice"
                                    class="btn-circle"
                                    style="cursor:pointer"
                                    variant="danger"
                                    @click="downloadInvoicePDF"
                                >
                                    <i v-if="downloadLoadingInvoice" class="fas fa-spinner fa-spin fa-fw fa-lg"></i>
                                    <i v-else class="fas fa-file-pdf fa-fw fa-lg"></i>
                                </b-button>
                                <b-button
                                    v-else
                                    class="btn-circle"
                                    style="cursor:not-allowed"
                                    variant="danger"
                                    disabled
                                >
                                    <i class="fas fa-file-pdf fa-fw fa-lg"></i>
                                </b-button>
                                <p>
                                    <small>
                                        {{ $t('org.billing.PDF of Usage Statement') }}
                                    </small>
                                </p>
                            </b-col>
                        </b-row>
                    </template>
                </SideBar>
            </b-overlay>
        </b-col>
        <b-col :xl="9" :lg="8" :md="7">
            <b-row class="match-height">
                <ProductCard
                    v-show="expanded !== 'region' "
                    :data="products"
                    :expanded="expanded"
                    @detail-layer="detailLayer"
                    @expand-panel="expandPanel"
                >
                </ProductCard>
                <RegionCard
                    v-show="expanded !== 'product'"
                    :data="regions"
                    :expanded="expanded"
                    @detail-layer="detailLayer"
                    @expand-panel="expandPanel"
                >
                </RegionCard>
                <div
                    class="col-12"
                >
                    <b-overlay
                        :show="status === 'loading'"
                    >
                        <DailyLine
                            :data="dates"
                            :projectIdx="project ? project.projectIdx : 0"
                            :status="status.toString()"
                            :month="month"
                            @detail-layer="detailLayer"
                        ></DailyLine>
                    </b-overlay>
                </div>
                <div
                    class="col-12"
                >
                    <b-overlay
                        :show="status === 'loading'"
                    >
                        <WeeklyHeatmap
                            :data="weeks"
                            :projectIdx="project ? project.projectIdx : 0"
                            :status="status.toString()"
                            :month="month"
                        >
                        </WeeklyHeatmap>
                    </b-overlay>
                </div>
            </b-row>
        </b-col>
        <b-modal
            ref="DOWNLOAD_DETAIL_CONFIRM_MODAL"
            ok-title="Submit"
            centered
            size="md"
            :title="$t('org.billing.Usage Details Download')"
            @ok="downloadDetailCSV"
        >
            <b-card-text>
                {{ $t('org.message.You will receive CSV file by email shortly, along with a download link of file') }}
            </b-card-text>
        </b-modal>
        <b-modal
            modalClass="modal-card-detail"
            ref="SUMMART_DETAIL_MODAL"
            ok-only
            ok-title="Close"
            centered
            size="lg"
            :title="detailLayerTitle"
        >
            <div v-for="(item, itemIdx) in detail.data" :key="itemIdx">
                <h5 class="p-25" :class="{ 'mt-1': itemIdx > 0 }">
                    {{ item.key }}
                </h5>
                <p class="mb-0 p-25"
                    v-for="(detailItem, detailItemIdx) in item.detail" :key="detailItemIdx"
                    :style="{'border-top': (detailItemIdx !== 0 && detailItemIdx % 5 === 0 ) ? '1px solid ' + themeColors.secondary : ''}"
                >
                    <b class="float-right">
                        {{ $_getDollar(detailItem.cost) }}
                    </b>
                    {{ detailItem.itemDescription }}
                    <span
                        v-if="isNaN(detailItem.unblendedRate)"
                    >
                        (no rate)
                    </span>
                    <span v-else>
                        ({{ detailItem.unblendedRate }})
                    </span>
                    <small class="text-nowrap">
                        x {{ $_getNumberFormat(detailItem.usageAmount) }}
                    </small>
                </p>
            </div>
        </b-modal>
    </b-row>
</template>

<script>
import { heightFade } from '@core/directives/animations'
import ToastificationContent from '@core/components/toastification/ToastificationContent'
import SideBar from './_components/SideBar'
import { ref, computed, getCurrentInstance, toRefs, inject, watch } from '@vue/composition-api'
import RegionCard from './_components/RegionCard'
import ProductCard from './_components/ProductCard'
import DailyLine from './_components/DailyLine'
import WeeklyHeatmap from './_components/WeeklyHeatmap'

export default {
    name: 'SummaryModule',
    components: {
        SideBar,
        RegionCard,
        ProductCard,
        DailyLine,
        WeeklyHeatmap
    },
    directives: {
        'height-fade': heightFade
    },
    props: {
        project: {
            type: Object,
            default: function () {
                return {}
            }
        },
        month: {
            type: String,
            default: ''
        }
    },
    setup (props, context) {
        const vm = getCurrentInstance().proxy
        const store = vm.$store

        const themeColors = inject('themeColors')
        const theme = inject('theme')
        const { project, month } = toRefs(props)

        /** data */
        const DOWNLOAD_DETAIL_CONFIRM_MODAL = ref(null)
        const SUMMART_DETAIL_MODAL = ref(null)
        const detailLayerTitle = ref(null)
        const expanded = ref(null)

        /** computed */
        const downloadLoadingSimple = computed(() => store.getters['$_summary/downloadLoadingSimple'])
        const downloadLoadingDetail = computed(() => store.getters['$_summary/downloadLoadingDetail'])
        const downloadLoadingInvoice = computed(() => store.getters['$_summary/downloadLoadingInvoice'])
        const projectIdxs = computed(() => store.getters.projectIdxs)
        const currentSummary = computed(() => store.getters['$_summary/currentSummary'])
        const estimatedSummary = computed(() => store.getters['$_summary/estimatedSummary'])
        const regions = computed(() => store.getters['$_summary/regions'])
        const products = computed(() => store.getters['$_summary/products'])
        const months = computed(() => store.getters['$_summary/months'])
        const weeks = computed(() => store.getters['$_summary/weeks'])
        const dates = computed(() => store.getters['$_summary/dates'])
        const status = computed(() => store.getters['$_summary/status'])
        const detail = computed(() => store.getters['$_summary/detail'])
        const allMonths = computed(() => store.getters.months)
        const hasInvoice = computed(() => store.getters['$_summary/hasInvoice'])
        const disabledButton = computed(() => project?.value?.projectIdx ? undefined : 'disabled')
        const disabledStyle = computed(() => ({ cursor: disabledButton.value === 'disabled' ? 'not-allowed' : 'pointer' }))
        const locale = computed(() => vm.$i18n.locale)

        /** methods */
        const getSummary = ({ projectIdx, month }) => store.dispatch('$_summary/getSummary', { projectIdx, month })
        const getSummaryDetail = (params) => store.dispatch('$_summary/getSummaryDetail', params)
        const getAvailableInvoice = ({ usageAccountId, month }) => store.dispatch('$_summary/getAvailableInvoice', { usageAccountId, month })
        const changeMonth = newMonth => context.emit('change-month', newMonth)
        const expandPanel = (panel) => {
            expanded.value = panel
        }
        const detailLayer = (param) => {
            const projectIdx = project?.value?.projectIdx ? [project.value.projectIdx] : projectIdxs.value
            detailLayerTitle.value = [param.productCode, param.region, param.date].filter(item => item !== undefined && item !== '').join(', ')

            getSummaryDetail({ projectIdx, month: month.value, ...param }).then(() => {
                if (SUMMART_DETAIL_MODAL.value) {
                    SUMMART_DETAIL_MODAL.value.show()
                }
            })
        }
        const showDownloadDetail = () => {
            if (DOWNLOAD_DETAIL_CONFIRM_MODAL.value) {
                DOWNLOAD_DETAIL_CONFIRM_MODAL.value.show()
            }
        }

        /** download api call methods */
        const downloadSimpleExcel = () => {
            const usageAccountId = project?.value.usageAccountId
            store.dispatch('$_summary/downloadSimpleExcel', { usageAccountId, month: month.value }).then(data => {
                const fileURL = window.URL.createObjectURL(new Blob([data]))
                const fileLink = document.createElement('a')
                fileLink.href = fileURL
                fileLink.setAttribute('download', `${usageAccountId}-AWS-Simple.xlsx`)
                document.body.appendChild(fileLink)
                fileLink.click()
            })
        }
        const downloadDetailCSV = () => {
            const usageAccountId = project?.value.usageAccountId
            store.dispatch('$_summary/downloadDetailCSV', { usageAccountId, month: month.value }).then(() => {
                vm.$toast({
                    component: ToastificationContent,
                    position: 'top-right',
                    props: {
                        title: vm.$t('org.message.Creating CSV file'),
                        icon: 'CoffeeIcon',
                        variant: 'success'
                    }
                })
            })
        }
        const downloadInvoicePDF = () => {
            const usageAccountId = project?.value.usageAccountId
            store.dispatch('$_summary/downloadInvoicePDF', { usageAccountId, month: month.value, locale: locale.value }).then(data => {
                const fileURL = window.URL.createObjectURL(new Blob([data]))
                const fileLink = document.createElement('a')
                fileLink.href = fileURL
                fileLink.setAttribute('download', 'invoice.pdf')
                document.body.appendChild(fileLink)
                fileLink.click()
            })
        }

        /** watch */
        watch(project, (value, oldValue) => {
            const projectIdx = value ? [value.projectIdx] : projectIdxs.value
            getSummary({ projectIdx, month: month.value }).then(() => {
                if (value?.projectIdx) {
                    getAvailableInvoice({ usageAccountId: value.usageAccountId, month: month.value })
                }
                expanded.value = null
            })
        }, {
            immediate: false,
            deep: true
        })
        watch(month, (value, oldValue) => {
            const projectIdx = project?.value?.projectIdx ? [project.value.projectIdx] : projectIdxs.value
            getSummary({ projectIdx, month: value }).then(() => {
                if (project?.value.projectIdx) {
                    getAvailableInvoice({ usageAccountId: project.value.usageAccountId, month: value })
                }
                expanded.value = null
            })
        }, {
            immediate: false,
            deep: true
        })
        return {
            themeColors,
            theme,
            DOWNLOAD_DETAIL_CONFIRM_MODAL,
            SUMMART_DETAIL_MODAL,
            detailLayerTitle,
            expanded,
            currentSummary,
            estimatedSummary,
            regions,
            products,
            months,
            weeks,
            dates,
            status,
            detail,
            allMonths,
            hasInvoice,
            disabledButton,
            disabledStyle,
            changeMonth,
            expandPanel,
            detailLayer,
            showDownloadDetail,
            downloadLoadingSimple,
            downloadLoadingDetail,
            downloadLoadingInvoice,
            downloadSimpleExcel,
            downloadDetailCSV,
            downloadInvoicePDF
        }
    }
}
</script>

<style scoped>
.btn-circle {
    border-radius: 50% !important;
    padding: 12px 9px;
}
</style>
